import { CreateCard } from '@/components/admin/versioned/create-card';
import { VersionedCardTile } from '@/components/admin/versioned/versioned-card';
import { useIsVitrine } from '@/lib/use-instance';
import type { VersionedWording } from '@/lib/versioned-labels';
import type { VersionTag, VersionedCard } from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * The grid of a library: the dashed card, then the cards (refonte modulaire §4).
 *
 * Its own component rather than three lines inside the route, for one property
 * worth a test: the creation card is ALWAYS the first child, on a full grid and
 * on an empty one. Routes are excluded from the coverage report by
 * `vitest.config.ts`, so a grid assembled in the route would be a rule nothing
 * could assert - and « en première position » is a sentence of §4 (issue #113,
 * 2026-09-21: it said « en dernière position » before), not a preference.
 *
 * Three columns on a desktop, two from `sm`, one on a phone: three 33 % columns
 * at 390 px would break « Alignement avec la question » onto four lines, and
 * `admin-responsive.spec.ts` holds every admin screen at zero lateral overflow.
 */

export interface VersionedGridProps<Card extends VersionedCard> {
  wording: VersionedWording;
  cards: readonly Card[];
  /** Why the grid is empty, worded by the caller - only it knows if a filter is armed. */
  emptyMessage: string;
  /** The object's own facets, per card: « 3 exemples » (see `versioned-card.tsx`). */
  facetsOf: (card: Card) => readonly string[];
  /** Beside the chevron. Absent: the entity's name. The persona's avatar (§15). */
  renderIdentity?: (card: Card) => ReactNode;
  /** Between the identity row and the pastilles. The persona's mini-gauge (§15). */
  renderExtra?: (card: Card) => ReactNode;
  /** Between the line of facts and the stamp. The experience's warning lines (§15). */
  renderFooter?: (card: Card) => ReactNode;
  /** The link a card opens - the caller builds it, the layer dresses it. */
  renderCardLink: (card: Card, props: { className: string; children: ReactNode }) => ReactNode;
  /** The link the dashed card opens. */
  renderCreateLink: (props: {
    className: string;
    'data-testid': string;
    children: ReactNode;
  }) => ReactNode;
  onRestore: (card: Card) => void;
  /** The id being restored right now, or null. */
  restoringId: string | null;
  /**
   * « Dupliquer », with the TAG the copy is taken from (§4.2).
   *
   * Optional: a library that has not wired the gesture shows no « … » menu at
   * all, rather than one whose only entry does nothing.
   */
  onDuplicate?: (card: Card, tag: VersionTag) => void;
  /** The id being duplicated right now, or null. */
  duplicatingId?: string | null;
}

export function VersionedGrid<Card extends VersionedCard>({
  wording,
  cards,
  emptyMessage,
  facetsOf,
  renderIdentity,
  renderExtra,
  renderFooter,
  renderCardLink,
  renderCreateLink,
  onRestore,
  restoringId,
  onDuplicate,
  duplicatingId = null,
}: VersionedGridProps<Card>) {
  // The vitrine edits no model (docs/specs/vitrine.md section 7): no card to
  // create, and no « … » menu to duplicate from.
  const vitrine = useIsVitrine();
  return (
    <div className="space-y-4">
      {cards.length === 0 ? (
        <p data-testid={`${wording.list}-vide`} className="text-gray-700 text-sm">
          {emptyMessage}
        </p>
      ) : null}
      <div
        data-testid={`${wording.list}-grille`}
        className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        {vitrine ? null : <CreateCard wording={wording} renderLink={renderCreateLink} />}
        {cards.map((card) => (
          <VersionedCardTile
            key={card.id}
            wording={wording}
            card={card}
            facets={facetsOf(card)}
            /*
              Spread and not `renderIdentity={renderIdentity}`, and that is
              `exactOptionalPropertyTypes`: an optional prop refuses an explicit
              `undefined`, so an absent slot has to arrive as an absent KEY. Same
              spelling as the optional `note` of a save request.
            */
            {...(renderIdentity === undefined ? {} : { renderIdentity })}
            {...(renderExtra === undefined ? {} : { renderExtra })}
            {...(renderFooter === undefined ? {} : { renderFooter })}
            {...(onDuplicate === undefined || vitrine ? {} : { onDuplicate })}
            renderLink={(props) => renderCardLink(card, props)}
            onRestore={onRestore}
            restoring={restoringId === card.id}
            duplicating={duplicatingId === card.id}
          />
        ))}
      </div>
    </div>
  );
}
